import {
  AutoVehicleType,
  mapSuperDispatchVehicleType,
} from 'lib/autoTransport/autoVehicleTypes';
import { Maybe } from 'types/UtilityTypes';

import { sanitizeAutoTransportDetails as sanitizeAutoTransportDetailsBase } from './autoTransportVehicleHelpers';

export type SuperDispatchCatalogEntry = {
  name: string;
  vehicleType: string;
};

type SuperDispatchVehicleCatalog = Record<string, SuperDispatchCatalogEntry[]>;

enum AutoVehicleClass {
  Car = 'car',
  Suv = 'suv',
  Pickup = 'pickup',
  Van = 'van',
}

const CLASS_TYPE_PREFERENCE: Record<
  AutoVehicleClass,
  readonly AutoVehicleType[]
> = {
  [AutoVehicleClass.Car]: [AutoVehicleType.Sedan, AutoVehicleType.Coupe2Doors],
  [AutoVehicleClass.Suv]: [AutoVehicleType.Suv],
  [AutoVehicleClass.Pickup]: [
    AutoVehicleType.Pickup4Doors,
    AutoVehicleType.Pickup2Doors,
  ],
  [AutoVehicleClass.Van]: [AutoVehicleType.Van],
};

let catalogData: Maybe<SuperDispatchVehicleCatalog> = null;
let catalogLoadPromise: Maybe<Promise<SuperDispatchVehicleCatalog>> = null;
let makesByLower = new Map<string, string>();
let cachedAutoMakes: string[] = [];

function rebuildMakeIndex(data: SuperDispatchVehicleCatalog) {
  makesByLower = new Map(
    Object.keys(data).map((make) => [make.toLowerCase(), make])
  );
  cachedAutoMakes = Object.keys(data).sort((left, right) =>
    left.localeCompare(right)
  );
}

function requireCatalogData(): SuperDispatchVehicleCatalog {
  if (!catalogData) {
    throw new Error(
      'Super Dispatch vehicle catalog is not loaded. Call loadSuperDispatchVehicleCatalog() first.'
    );
  }

  return catalogData;
}

export function isSuperDispatchVehicleCatalogLoaded(): boolean {
  return catalogData != null;
}

export const SUPER_DISPATCH_VEHICLE_CATALOG_LOAD_ERROR_MESSAGE =
  "Couldn't load the vehicle list. Refresh the sidebar and try again.";

export function loadSuperDispatchVehicleCatalog(): Promise<SuperDispatchVehicleCatalog> {
  if (catalogData) {
    return Promise.resolve(catalogData);
  }

  if (!catalogLoadPromise) {
    catalogLoadPromise = import(
      /* webpackChunkName: "super-dispatch-vehicle-catalog" */
      './autoTransportCatalog.json'
    )
      .then((module) => {
        catalogData = module.default as SuperDispatchVehicleCatalog;
        rebuildMakeIndex(catalogData);
        return catalogData;
      })
      .catch((error) => {
        catalogLoadPromise = null;
        return Promise.reject(error);
      });
  }

  return catalogLoadPromise;
}

export function getAutoMakes(): readonly string[] {
  return cachedAutoMakes;
}

export function sanitizeAutoTransportDetails(
  details: Parameters<typeof sanitizeAutoTransportDetailsBase>[0]
) {
  return sanitizeAutoTransportDetailsBase(details, {
    resolveMake: resolveCatalogMake,
    resolveModel: resolveCatalogModel,
  });
}

// Send the catalog's spelling for make and model. Form state keeps what the
// suggestion said so suggested vs applied stays comparable.
export function canonicalizeAutoTransportCatalogNames<
  T extends { vehicles: Array<{ make: string; model: string }> },
>(details: T): T {
  return {
    ...details,
    vehicles: details.vehicles.map((vehicle) => {
      const make = resolveCatalogMake(vehicle.make) ?? vehicle.make;
      const model = resolveCatalogModel(make, vehicle.model) ?? vehicle.model;
      return { ...vehicle, make, model };
    }),
  };
}

export function resolveCatalogMake(make: string): Maybe<string> {
  if (!catalogData) {
    return null;
  }

  const trimmed = make.trim();
  if (!trimmed) {
    return null;
  }

  return makesByLower.get(trimmed.toLowerCase()) ?? null;
}

export function getModelNamesForMake(make: string): string[] {
  const resolvedMake = resolveCatalogMake(make);
  if (!resolvedMake) {
    return [];
  }

  const entries = requireCatalogData()[resolvedMake] ?? [];
  const seen = new Set<string>();
  const names: string[] = [];

  for (const entry of entries) {
    if (seen.has(entry.name)) {
      continue;
    }

    seen.add(entry.name);
    names.push(entry.name);
  }

  return names.sort((left, right) => left.localeCompare(right));
}

export function resolveCatalogModel(
  make: string,
  model: string
): Maybe<string> {
  const trimmed = model.trim();
  if (!trimmed) {
    return null;
  }

  const names = getModelNamesForMake(make);
  return (
    names.find((name) => name.toLowerCase() === trimmed.toLowerCase()) ?? null
  );
}

function vehicleClass(vehicleType: AutoVehicleType): AutoVehicleClass {
  switch (vehicleType) {
    case AutoVehicleType.Sedan:
    case AutoVehicleType.Coupe2Doors:
      return AutoVehicleClass.Car;
    case AutoVehicleType.Suv:
      return AutoVehicleClass.Suv;
    case AutoVehicleType.Pickup2Doors:
    case AutoVehicleType.Pickup4Doors:
      return AutoVehicleClass.Pickup;
    case AutoVehicleType.Van:
      return AutoVehicleClass.Van;
  }
}

function pickTypeWithinClass(
  types: Set<AutoVehicleType>
): Maybe<AutoVehicleType> {
  const classes = new Set([...types].map(vehicleClass));
  if (classes.size !== 1) {
    return null;
  }

  const [vehicleClassName] = [...classes];
  for (const preferred of CLASS_TYPE_PREFERENCE[vehicleClassName]) {
    if (types.has(preferred)) {
      return preferred;
    }
  }

  return null;
}

export function inferVehicleTypeForMakeModel(
  make: string,
  model: string
): Maybe<AutoVehicleType> {
  const resolvedMake = resolveCatalogMake(make);
  if (!resolvedMake) {
    return null;
  }

  const normalizedModel = model.trim().toLowerCase();
  const entries = (requireCatalogData()[resolvedMake] ?? []).filter(
    (entry) => entry.name.trim().toLowerCase() === normalizedModel
  );
  if (entries.length === 0) {
    return null;
  }

  const mappedTypes = new Set<AutoVehicleType>();
  for (const entry of entries) {
    const mapped = mapSuperDispatchVehicleType(entry.vehicleType);
    if (mapped) {
      mappedTypes.add(mapped);
    }
  }

  if (mappedTypes.size === 0) {
    return null;
  }

  return pickTypeWithinClass(mappedTypes);
}
